import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";
import { resolveWorkflowRunRefNo, WorkflowRunRefNoResult } from "../services/workflowService";

export type WorkflowRunRefNoState =
  | { status: "idle" }
  | { status: "resolving" }
  | { status: "found"; instanceId: number }
  | { status: "not-found" }
  | { status: "forbidden" }
  | { status: "error" };

/**
 * Resolves a share link's RefNo to its case (decision 6.5). Pass `null` when the URL names no RefNo
 * and the hook stays idle. 404 / 403 come back as their own states; anything else goes through
 * useApi's central alert and lands in `error`, with `retry`.
 */
const useWorkflowRunRefNo = (refNo: string | null) => {
  const { executeApi } = useApi();
  const [state, setState] = useState<WorkflowRunRefNoState>({ status: refNo ? "resolving" : "idle" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!refNo) {
      setState({ status: "idle" });
      return undefined;
    }
    let active = true;
    setState({ status: "resolving" });
    executeApi<string, WorkflowRunRefNoResult>(resolveWorkflowRunRefNo, refNo).then((result) => {
      if (!active) return;
      if (!result) setState({ status: "error" });
      else if (result.kind === "found") setState({ status: "found", instanceId: result.instanceId });
      else setState({ status: result.kind });
    });
    return () => {
      active = false;
    };
  }, [executeApi, refNo, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return { state, retry };
};

export default useWorkflowRunRefNo;
